import type { Deal } from "@/types/deal";

export function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" || trimmed === "None%";
  }
  if (typeof value === "number") return value === 0;
  return false;
}

export function getSponsorName(deal: Deal): string {
  const sponsor = deal.sponsor;
  if (sponsor && typeof sponsor === "object" && "sponsor_name" in sponsor) {
    return sponsor.sponsor_name?.trim() ?? "";
  }
  if (typeof sponsor === "string") return sponsor.trim();
  return "";
}

export function getTransactionTypeLabel(deal: Deal): string {
  return deal.transaction_type?.transaction_type?.trim() ?? "";
}

export function isRefinancingDeal(deal: Deal): boolean {
  return getTransactionTypeLabel(deal).toLowerCase().includes("refinanc");
}

export function getCurrencySymbol(currencyCode: string | null | undefined): string {
  switch (currencyCode?.toUpperCase()) {
    case "GBP":
      return "£";
    case "EUR":
      return "€";
    case "USD":
      return "$";
    default:
      return "";
  }
}

export function getCurrencyDisplayName(
  currencyCode: string | null | undefined,
): string {
  switch (currencyCode?.toUpperCase()) {
    case "GBP":
      return "Sterling";
    case "EUR":
      return "Euro";
    case "USD":
      return "US Dollar";
    default:
      return currencyCode ?? "";
  }
}

export function getInterestBenchmark(
  currencyCode: string | null | undefined,
): string {
  switch (currencyCode?.toUpperCase()) {
    case "GBP":
      return "3M SONIA";
    case "EUR":
      return "3-month EURIBOR";
    case "USD":
      return "SOFR";
    default:
      return "EURIBOR";
  }
}

export function formatMoney(
  amount: number | null | undefined,
  currencyCode: string | null | undefined,
): string {
  if (amount == null) return "";
  const symbol = getCurrencySymbol(currencyCode);
  return `${symbol}${amount.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
}

export function formatPercent(value: number | null | undefined): string {
  if (isBlank(value)) return "";
  return `${value}%`;
}

export function formatMultiplier(value: number | null | undefined): string {
  if (isBlank(value)) return "";
  return `${value}x`;
}

export function joinBorrowerNames(
  primary: Array<{ name?: string | null }> | undefined,
  secondary: Array<{ name?: string | null }> | undefined,
): string[] {
  const names = [...(primary ?? []), ...(secondary ?? [])]
    .map((borrower) => borrower.name?.trim())
    .filter((name): name is string => Boolean(name));

  return [...new Set(names)];
}

/** Converts internal year keys (e.g. year_1, year1) to display labels (e.g. year 1). */
export function formatYearLabel(yearKey: string): string {
  const match = yearKey.trim().match(/^year[\s_-]*(\d+)$/i);
  if (match) {
    return `year ${match[1]}`;
  }
  return yearKey.replace(/_/g, " ");
}

export function formatYearBasedLines(
  years: Record<string, number> | null | undefined,
  singleValue: number | null | undefined,
  suffix: "%" | "x" = "%",
): string {
  if (years && Object.keys(years).length > 0) {
    return Object.entries(years)
      .sort(([left], [right]) => {
        const leftNum = Number(left.replace(/\D/g, ""));
        const rightNum = Number(right.replace(/\D/g, ""));
        return leftNum - rightNum;
      })
      .map(([year, value]) => {
        const formatted =
          suffix === "x" ? formatMultiplier(value) : formatPercent(value);
        return formatted ? `${formatted} in ${formatYearLabel(year)}` : "";
      })
      .filter(Boolean)
      .join("\n");
  }

  if (!isBlank(singleValue)) {
    return suffix === "x"
      ? formatMultiplier(singleValue)
      : formatPercent(singleValue);
  }

  return "";
}

/** Formats items as (a), (b), (c) lines with tab indent, matching the Porto term sheet style. */
export function formatLetteredList(items: string[]): string {
  return items
    .map((item, index) => {
      const letter = String.fromCharCode(97 + index);
      let text = item.trim();
      const isLast = index === items.length - 1;

      if (!isLast && !text.endsWith(";")) {
        text += ";";
      }

      return `(${letter})\t${text}`;
    })
    .join("\n");
}
